import { secondsToMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NetworkQualityStats as TwilioNetworkQualityStats } from 'twilio-video';

import { UnstableInternetToastWrapper as UnstableInternetToast } from '../components/UnstableInternetToast';
import { sendDataDogEvent } from '../datadog/events';
import { logger } from '../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../events';
import { toast } from '../toasts';
import type { NetworkQualityChangeHandler, RemoteParticipant, RoomDelegator } from '../video-sdk';
import {
  LocalMediaStatus,
  localVideoStatusAtom,
  parseIdentity,
  participantsAtom,
  roomAtom,
  roomToDiagnosticInfo,
} from '../video-sdk';
import { TWILIO_NETWORK_QUALITY_STATS_CLASS_NAME, getTwilioNetworkQualityStats } from '../video-sdk/twilio';

const UNSTABLE_INTERNET_TOAST_KEY = 'unstable-internet-toast';

/**
 * Type guard to check if the given stats object is a Twilio
 * NetworkQualityStats. Using `instanceof` on `TwilioNetworkQualityStats` does
 * not work as expected.
 */
function satisfiesNetworkQualityStats(stats: unknown): stats is TwilioNetworkQualityStats {
  return stats?.constructor?.name === TWILIO_NETWORK_QUALITY_STATS_CLASS_NAME;
}

function emitNetworkQualityEvents(
  type: 'local' | 'remote',
  room: RoomDelegator,
  stats?: unknown,
  additionalCtx?: object,
) {
  if (!stats) return;

  // Zoom doesn't provide detailed Network Quality stats like Twilio
  const networkQualityStats = satisfiesNetworkQualityStats(stats) ? getTwilioNetworkQualityStats(stats) : {};

  const ctx = {
    ...roomToDiagnosticInfo(room),
    ...additionalCtx,
    ...networkQualityStats,
  };

  sendDataDogEvent(`twilio.networkQuality.${type}`, ctx);
  logger.debug('Network quality changed', ctx);
}

const QUALITY_LEVEL_THRESHOLD = 1;
const POOR_QUALITY_DURATION_THRESHOLD = secondsToMilliseconds(15);

export function useNetworkQualityLogging({ onOpenSettings }: { onOpenSettings: () => void }) {
  const room = useAtomValue(roomAtom);
  const remoteParticipants = useAtomValue(participantsAtom);
  const getLocalVideoTrackState = useAtomCallback(useCallback((get) => get(localVideoStatusAtom), []));
  const sentNetworkQualityToastRef = useRef(false);
  const [isNetworkQualityPoor, setIsNetworkQualityPoor] = useState(true);

  useEffect(
    function popInternetUnstableToast() {
      if (isNetworkQualityPoor && !sentNetworkQualityToastRef.current) {
        // Trigger the toast if network quality is poor for 15 seconds and user video is on
        const timeoutId = setTimeout(() => {
          if (getLocalVideoTrackState() === LocalMediaStatus.READY) {
            sentNetworkQualityToastRef.current = true;
            sendLoggingEvents(TrackingEvents.ANY_CONNECTIVITY_ISSUE_NOTIF);
            toast.enqueue(
              (ariaProps) => (
                <UnstableInternetToast
                  {...ariaProps}
                  onOpenSettings={() => {
                    toast.close(UNSTABLE_INTERNET_TOAST_KEY);
                    onOpenSettings();
                  }}
                />
              ),
              {
                key: UNSTABLE_INTERNET_TOAST_KEY,
                timeout: Infinity,
                purpose: 'system',
              },
            );
          }
        }, POOR_QUALITY_DURATION_THRESHOLD);

        return () => {
          // If network quality improves, clear the timeout and don't trigger the toast
          clearTimeout(timeoutId);
        };
      }
    },
    [isNetworkQualityPoor, getLocalVideoTrackState, onOpenSettings],
  );

  useEffect(
    function handleLocalNetworkQualityLevelChanges() {
      if (!room || !room.localParticipant) return;
      const localParticipant = room.localParticipant;

      const logLocalNetworkQualityStatsAndControlToast: NetworkQualityChangeHandler = (
        networkQualityLevel,
        networkQualityStats,
      ) => {
        emitNetworkQualityEvents('local', room, networkQualityStats);

        // Stop updating isNetworkQualityPoor if we aren't going to pop a toast for it
        if (sentNetworkQualityToastRef.current) {
          return;
        }

        if (networkQualityLevel > QUALITY_LEVEL_THRESHOLD) {
          setIsNetworkQualityPoor(false);
        }

        if (networkQualityLevel <= QUALITY_LEVEL_THRESHOLD) {
          setIsNetworkQualityPoor(true);
        }
      };

      const detachEventListenerCallback = localParticipant.on(
        'networkQualityLevelChanged',
        logLocalNetworkQualityStatsAndControlToast,
      );

      return function cleanup() {
        detachEventListenerCallback();
      };
    },
    [room, isNetworkQualityPoor],
  );

  useEffect(
    function handleRemoteNetworkQualityLevelChanges() {
      if (!room) return;

      const remoteParticipantNetworkQualityHandlers: Array<[RemoteParticipant, NetworkQualityChangeHandler]> = [];

      const detachListenerCallbacks = remoteParticipants.map((participant) => {
        const logRemoteNetworkQualityStats: NetworkQualityChangeHandler = (
          _networkQualityLevel,
          networkQualityStats,
        ) => {
          const { id: remoteUserId, userType: remoteUserType } = parseIdentity(participant.identity);

          emitNetworkQualityEvents('remote', room, networkQualityStats, {
            remoteParticipantSid: participant.sid,
            remoteUserId,
            remoteUserType,
          });
        };
        remoteParticipantNetworkQualityHandlers.push([participant, logRemoteNetworkQualityStats]);

        return participant.on('networkQualityLevelChanged', logRemoteNetworkQualityStats);
      });

      return function cleanup() {
        detachListenerCallbacks.forEach((detachCallback) => {
          detachCallback();
        });
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remoteParticipants, room],
  );
}
